/**
 * Home service for fetching destination data from assets.travley.in
 */

const CACHE_KEY = 'travley_home_data';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Check if cached data exists and is still valid
 * @returns {Array|null} - Cached destinations array or null if invalid/expired
 */
function getCachedHomeDestinations() {
  try {
    const cachedData = localStorage.getItem(CACHE_KEY);
    if (!cachedData) {
      return null;
    }

    const parsed = JSON.parse(cachedData);
    const now = new Date().getTime();

    // Check if cache has expired
    if (now - parsed.timestamp > CACHE_DURATION) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    // Validate cached data structure
    if (!Array.isArray(parsed.data)) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    return parsed.data;
  } catch (error) {
    // If there's any error parsing cached data, remove it
    localStorage.removeItem(CACHE_KEY);
    return null;
  }
}

/**
 * Cache home destinations data in localStorage
 * @param {Array} destinations - Array of destination objects to cache
 */
function cacheHomeDestinations(destinations) {
  try {
    const cacheData = {
      data: destinations,
      timestamp: new Date().getTime()
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    // Silently fail if localStorage is not available or full
    console.warn('Failed to cache home destinations:', error);
  }
}

/**
 * Fetch home destinations data from the deployed JSON file with caching
 * @returns {Promise<Array>} - Array of destination objects
 */
export async function fetchHomeDestinations() {
  // First, try to get cached data
  const cachedDestinations = getCachedHomeDestinations();
  if (cachedDestinations) {
    return cachedDestinations;
  }

  // If no valid cached data, fetch from API
  const response = await fetch('https://assets.travley.in/home.json');

  if (!response.ok) {
    throw new Error(`Failed to fetch home destinations: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();

  if (!Array.isArray(data)) {
    throw new Error('Invalid data format: expected array of destinations');
  }

  // Cache the fresh data
  cacheHomeDestinations(data);

  return data;
}

/**
 * Get destinations grouped by category
 * @param {Array} destinations - Array of destination objects
 * @returns {Object} - Object with categories as keys and destination arrays as values
 */
export function groupDestinationsByCategory(destinations) {
  const grouped = {};

  destinations.forEach(destination => {
    const category = destination.category || 'Other';
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(destination);
  });

  return grouped;
}

/**
 * Get unique categories from destinations
 * @param {Array} destinations - Array of destination objects
 * @returns {Array} - Array of unique category names
 */
export function getUniqueCategories(destinations) {
  const categories = new Set();

  destinations.forEach(destination => {
    if (destination.category) {
      categories.add(destination.category);
    }
  });

  return ['All', ...Array.from(categories).sort()];
}

/**
 * Get destination names for search suggestions
 * @param {Array} destinations - Array of destination objects
 * @returns {Array} - Array of destination names
 */
export function getDestinationNames(destinations) {
  return destinations
    .map(dest => dest.name)
    .filter(name => name && name.trim())
    .sort();
}

/**
 * Filter destinations by category
 * @param {Array} destinations - Array of destination objects
 * @param {string} category - Category to filter by
 * @returns {Array} - Filtered destinations
 */
export function filterDestinationsByCategory(destinations, category) {
  if (!category || category === 'All') {
    return destinations;
  }

  return destinations.filter(dest =>
    dest.category && dest.category.toLowerCase() === category.toLowerCase()
  );
}

/**
 * Search destinations by name or description
 * @param {Array} destinations - Array of destination objects
 * @param {string} query - Search query
 * @returns {Array} - Matching destinations
 */
export function searchDestinations(destinations, query) {
  if (!query || !query.trim()) {
    return destinations;
  }

  const searchTerm = query.toLowerCase().trim();

  return destinations.filter(dest => {
    const name = (dest.name || '').toLowerCase();
    const description = (dest.description || '').toLowerCase();
    const city = (dest.city || '').toLowerCase();
    const state = (dest.state || '').toLowerCase();

    return name.includes(searchTerm) ||
           description.includes(searchTerm) ||
           city.includes(searchTerm) ||
           state.includes(searchTerm);
  });
}
